import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const STAGES = ["Submitted", "In Review", "Quoted", "Paid", "Closed"];

function Pipeline({ status }) {
  const idx = STAGES.indexOf(status);
  return (
    <div className="flex items-center" data-testid="status-pipeline">
      {STAGES.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex flex-col items-center">
            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${i <= idx ? "bg-coral border-coral text-white" : "bg-white border-zinc-300 text-zinc-400"}`}>
              {i < idx ? <CheckCircle2 size={16} /> : i + 1}
            </div>
            <span className={`text-[10px] mt-1.5 font-semibold uppercase tracking-wide ${i <= idx ? "text-ink" : "text-zinc-400"}`}>{s}</span>
          </div>
          {i < STAGES.length - 1 && <div className={`flex-1 h-0.5 mx-1 -mt-5 ${i < idx ? "bg-coral" : "bg-zinc-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [r, q] = await Promise.all([api.get(`/requests/${id}`), api.get(`/requests/${id}/quotes`)]);
    setRequest(r.data);
    setQuotes(q.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const accept = async (qid) => {
    await api.post(`/quotes/${qid}/accept`);
    toast.success("Quote accepted. Our team will be in touch to finalize.");
    load();
  };

  if (loading) return <div className="text-zinc-400 py-20 text-center">Loading...</div>;
  if (!request) return <div className="py-20 text-center">Request not found.</div>;

  const closed = request.status === "Closed";

  return (
    <div className="max-w-3xl">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-coral mb-6 transition" data-testid="back-to-requests">
        <ArrowLeft size={15} /> Back to requests
      </Link>

      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-coral text-xs font-bold uppercase tracking-widest mb-1">{request.category}</p>
          <h1 className="font-serif text-3xl font-bold tracking-tight">{request.title}</h1>
        </div>
        <StatusBadge status={request.status} testid="detail-status" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 my-6">
        <Pipeline status={request.status} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <h3 className="font-serif text-lg font-bold">Request details</h3>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Detail label="Quantity" value={request.quantity} />
          <Detail label="Target price" value={request.target_price || "—"} />
        </div>
        <Detail label="Product details" value={request.product_details} />
        {request.notes && <Detail label="Notes" value={request.notes} />}
        {request.attachment_data && (
          <a href={request.attachment_data} download={request.attachment_name} className="inline-flex items-center gap-2 text-coral font-semibold text-sm" data-testid="download-attachment">
            <Paperclip size={15} /> {request.attachment_name}
          </a>
        )}
      </div>

      {/* QUOTES */}
      <div className="mt-8">
        <h3 className="font-serif text-2xl font-bold mb-4">Quotes</h3>
        {quotes.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center text-zinc-500" data-testid="no-quotes">
            <FileText className="mx-auto text-zinc-300 mb-3" size={36} strokeWidth={1.2} />
            No quotes yet. Our team is reviewing your request.
          </div>
        ) : (
          <div className="space-y-4">
            {quotes.map((q) => (
              <div key={q.id} className="bg-white border border-zinc-200 rounded-sm p-6" data-testid={`quote-${q.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-serif text-3xl font-bold">${q.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-sm font-sans text-zinc-400 uppercase">{q.currency}</span></div>
                    {q.lead_time && <div className="text-sm text-zinc-500 mt-1">Lead time: {q.lead_time}</div>}
                  </div>
                  <StatusBadge status={q.status} />
                </div>
                {q.notes && <p className="text-sm text-zinc-600 mt-3 border-t border-zinc-100 pt-3">{q.notes}</p>}

                {!closed && (
                  <div className="mt-5 pt-5 border-t border-zinc-100">
                    {q.status !== "accepted" ? (
                      <button onClick={() => accept(q.id)} className="bg-coral hover:bg-coral-hover text-white font-semibold px-6 py-3 rounded-sm transition" data-testid={`accept-quote-${q.id}`}>
                        Accept Quote
                      </button>
                    ) : (
                      <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm" data-testid={`quote-accepted-${q.id}`}>
                        <CheckCircle2 size={17} /> Quote accepted — our team will contact you to finalize.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
    <div className="text-zinc-800 mt-1 whitespace-pre-wrap">{value}</div>
  </div>
);
